import Vue from "vue";
import axios from "axios";
import { CACHED_CALL_STATUS, NOTIF_CALL_DURATION } from "@/common/const";
import { subscribe as subscribeCallEvents, init as initCallEvents } from "@/services/callEventsService";

// Global state
let globalDurationTimer = null;
const checkCallPromise = new Map(); // Track in-flight API requests
const RINGING_VERIFY_AFTER = 20; // seconds. Inbound calls ring ~10 s (15 s when transferred)

export default {
	namespaced: true,
	state: () => ({
		calls: {},
		durations: {},
		notifs: {},
		lastEventTime: {}
	}),

	getters: {
		queued:(state) =>  Object.values(state.calls).filter(call => call.status === "queued"),
		inbound: (state) => Object.values(state.calls).filter(call => call.status === "ringing"),
		active: (state) => Object.values(state.calls).filter(call => call.status === "in-progress"),
		parked: (state) => Object.values(state.calls).filter(call => call.status === "parked").sort((a, b) => a.parkNo - b.parkNo),
		durations: (state) => state.durations
	},

	mutations: {
		releaseCall(state, callId) {
			if (state.calls[callId]) {
				console.debug(`[CALLS] Releasing call ${callId}`);

				Vue.delete(state.calls, callId);
				delete state.durations[callId];
				delete state.notifs[callId];
				delete state.lastEventTime[callId];

				// Stop global timer if no more active calls
				if (!hasActiveCalls(state)) {
					stopGlobalDurationTimer(state);
				}
			}
		}
	},

	actions: {
		releaseCalls({ state, commit }) {
			// Release all calls from the store to start with fresh state
			const callIds = Object.keys(state.calls);

			callIds.forEach(callId => {
				commit("releaseCall", callId);
			});
		},

		setCall({ state, commit, dispatch }, call) {
			const callId = call.callId;

			if (call.status === "completed") {
				commit("releaseCall", callId);
				return;
			}

			console.debug("[CALLS] setCall", call);

			// Skip if we've already processed a newer or equal event for this call
			if (state.lastEventTime[callId] && call.updatedAt <= state.lastEventTime[callId]) {
				console.debug(`[CALLS] Skipping duplicate/old event for call ${callId}`);
				return;
			}

			// Update last event time
			state.lastEventTime[callId] = call.updatedAt;

			// Calculate duration using backend actionAt timestamp
			let duration = Math.ceil((Date.now() - (call.actionAt ?? call.updatedAt)) / 1000);
			
			if (call.status === "parked" && call.waitTime) {
				duration += call.waitTime;
			}

			Vue.set(state.durations, callId, duration);
			
			if (state.calls[callId]) {
				Vue.set(state.calls, callId, call);
			} else {
				Vue.set(state.calls, callId, call);

				// Set notification threshold using accurate duration
				state.notifs[callId] = duration + NOTIF_CALL_DURATION;
			}

			// A ringing card only lasts seconds: verify it early so it can never stay clickable
			if (call.status === "ringing") {
				state.notifs[callId] = duration + RINGING_VERIFY_AFTER;
			}

			// Start global timer if not already running
			startGlobalDurationTimer(state, () => dispatch("updateDurations"));
		},

		updateDurations({ state, dispatch }) {
			// Update all durations based on actionAt timestamps
			const now = Date.now();

			Object.entries(state.calls).forEach(([callId, call]) => {
				// Calculate duration from actionAt timestamp
				let duration = Math.floor((now - (call.actionAt ?? call.updatedAt)) / 1000);

				// For parked calls, include waitTime
				if (call.status === "parked" && call.waitTime) {
					duration += call.waitTime;
				}

				Vue.set(state.durations, callId, duration);

				// Check if we call still valid on backend
				if (
					state.durations[callId] >= state.notifs[callId] &&
					call.status !== CACHED_CALL_STATUS.COMPLETED
				) {
					// Defer the fetch to avoid blocking the UI update
					setTimeout(() => dispatch("checkCall", callId), 0);

					state.notifs[callId] += NOTIF_CALL_DURATION;
				}
			});
		},

		async checkCall({ commit, state }, callId) {
			if (checkCallPromise.has(callId)) {
				return checkCallPromise.get(callId);
			}

			const checked = state.calls[callId];

			const fetchPromise = axios
				.get(`/voice/v1/calls/${callId}`)
				.then(({ data: call }) => {
					const stillRinging = [CACHED_CALL_STATUS.INITIATED, CACHED_CALL_STATUS.RINGING].includes(call.status);
					// Ignore if a newer event replaced the card while the request was in flight
					const staleRinging = state.calls[callId] === checked && checked?.status === "ringing" && !stillRinging;

					if (call.status === CACHED_CALL_STATUS.COMPLETED || staleRinging) {
						commit("releaseCall", callId);
					}
				})
				.catch((error) => {
					if (error?.response?.status === 404) {
						commit("releaseCall", callId);
					} else {
						console.error(`[CALLS] Failed to fetch call ${callId}:`, error);
					}
				})
				.finally(() => {
					checkCallPromise.delete(callId);
				});

			checkCallPromise.set(callId, fetchPromise);

			return fetchPromise;
		}
	},

	init({ watch, login, dispatch, commit }, rootStore) {
		initCallEvents({
			dispatch,
			commit,
			clinicDataGetter: () => rootStore.getters["clinic/clinicData"]
		});

		watch(
			() => login.userGroup?.id,
			(newId, oldId) => {
				if (newId === oldId) return;
				if (newId) {
					subscribeCallEvents(newId);
				}
			}
		);

		watch(
			() => login.refreshed,
			(refreshed) => {
				if (refreshed) {
					subscribeCallEvents(login.userGroup.id);
				}
			}
		);
	}
};

/**
 * Start global duration timer that updates all active calls once per second
 * This replaces per-call intervals with a single shared timer
 */
function startGlobalDurationTimer(state, commitFn) {
	if (globalDurationTimer) return;

	globalDurationTimer = setInterval(() => {
		if (commitFn) {
			commitFn();
		}
	}, 1000);

	console.debug("[CALLS] Started global duration timer");
}

/**
 * Stop global timer if there are no active calls
 */
function stopGlobalDurationTimer(state) {
	if (globalDurationTimer) {
		clearInterval(globalDurationTimer);
		globalDurationTimer = null;
		console.debug("[CALLS] Stopped global duration timer");
	}
}

/**
 * Check if there are any active calls
 */
function hasActiveCalls(state) {
	return Object.keys(state.calls).length > 0;
}
